/**
 * @file Admin Cross-Tenant Conversation Overview
 *
 * Aggregate KPIs for the selected period/filters (including tenant), plus a paginated
 * conversation list.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { MessageOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import { useClientsWithCompaniesQuery } from '@/api/auth/queries';
import {
  useConversationOverviewConversationsQuery,
  useConversationOverviewTenantsQuery
} from '@/api/chat/queries';
import type {
  ConversationOverviewChannel,
  ConversationOverviewFlag
} from '@/api/chat/interfaces';
import {
  ConversationOverviewFilters,
  ConversationOverviewStats,
  ConversationOverviewTable,
  getDateRangeFromPeriod,
  type ConversationOverviewPeriod
} from '@/components/conversation-overview';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { booleanSearchParam } from '@/utils/searchParams';

const searchSchema = z.object({
  period: z.enum(['today', '7d', '30d', 'custom']).catch('7d'),
  from: z.string().optional(),
  to: z.string().optional(),
  channel: z.enum(['WEBCHAT', 'VOICE', 'EMAIL']).optional(),
  flag: z.enum(['RED', 'GREEN']).optional(),
  humanHandoff: booleanSearchParam(),
  technicalIssue: booleanSearchParam(),
  clientId: z.string().optional(),
  page: z.coerce.number().int().min(1).catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20)
});

export const Route = createFileRoute('/admin/conversation-overview/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: ConversationOverviewPage
});

function ConversationOverviewPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refetchInterval = autoRefresh ? AUTOREFRESH_INTERVAL : false;

  const customRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    if (search.period !== 'custom' || !search.from || !search.to) return null;
    return [dayjs(search.from), dayjs(search.to)];
  }, [search.period, search.from, search.to]);

  const range = useMemo(
    () => getDateRangeFromPeriod(search.period as ConversationOverviewPeriod, customRange),
    [search.period, customRange]
  );

  const sharedFilters = useMemo(
    () => ({
      from: range?.[0]?.toISOString() ?? '',
      to: range?.[1]?.toISOString() ?? '',
      channel: search.channel as ConversationOverviewChannel | undefined,
      flag: search.flag as ConversationOverviewFlag | undefined,
      humanHandoff: search.humanHandoff,
      technicalIssue: search.technicalIssue
    }),
    [range, search.channel, search.flag, search.humanHandoff, search.technicalIssue]
  );

  const conversationFilters = useMemo(
    () => ({
      ...sharedFilters,
      clientId: search.clientId,
      page: search.page,
      pageSize: search.pageSize
    }),
    [sharedFilters, search.clientId, search.page, search.pageSize]
  );

  const { data: clientsData } = useClientsWithCompaniesQuery({ page: 1, limit: 500 });

  const {
    data: tenantsData,
    isLoading: tenantsLoading,
    isFetching: tenantsFetching,
    dataUpdatedAt: tenantsUpdatedAt
  } = useConversationOverviewTenantsQuery(sharedFilters, !!range, refetchInterval);

  const {
    data: conversationsData,
    isLoading: conversationsLoading,
    isError: conversationsError,
    isFetching: conversationsFetching,
    dataUpdatedAt: conversationsUpdatedAt
  } = useConversationOverviewConversationsQuery(conversationFilters, !!range, refetchInterval);

  const lastUpdatedText = useLastUpdatedText(Math.max(tenantsUpdatedAt || 0, conversationsUpdatedAt || 0));

  const clientsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clientsData?.clients ?? []) {
      map.set(client.id, client.name);
    }
    return map;
  }, [clientsData]);

  const tenantOptions = useMemo(
    () =>
      (clientsData?.clients ?? []).map((client) => ({
        value: client.id,
        label: client.name
      })),
    [clientsData]
  );

  const aggregateStats = useMemo(() => {
    const allRows = tenantsData?.tenants ?? [];
    const rows = search.clientId
      ? allRows.filter((row) => row.clientId === search.clientId)
      : allRows;
    const totalConversations = rows.reduce((sum, row) => sum + row.totalConversations, 0);
    const technicalIssueCount = rows.reduce((sum, row) => sum + row.technicalIssueCount, 0);
    const humanHandoffCount = rows.reduce((sum, row) => sum + row.humanHandoffCount, 0);
    const redFlagCount = rows.reduce((sum, row) => sum + row.redFlagCount, 0);
    const percent = (count: number) =>
      totalConversations > 0 ? Math.round((count / totalConversations) * 100) : 0;
    return {
      totalConversations,
      technicalIssueCount,
      technicalIssuePercent: percent(technicalIssueCount),
      humanHandoffCount,
      humanHandoffPercent: percent(humanHandoffCount),
      redFlagCount,
      redFlagPercent: percent(redFlagCount)
    };
  }, [tenantsData, search.clientId]);

  const hasActiveFilters =
    !!search.clientId ||
    !!search.channel ||
    !!search.flag ||
    search.humanHandoff !== undefined ||
    search.technicalIssue !== undefined;

  const updateSearch = (patch: Record<string, unknown>) => {
    void navigate({
      search: (prev) => {
        const next = { ...prev, ...patch };
        for (const key of Object.keys(next) as (keyof typeof next)[]) {
          if (next[key] === undefined) delete next[key];
        }
        return next as typeof prev;
      }
    });
  };

  const clearFilters = () => {
    updateSearch({
      clientId: undefined,
      channel: undefined,
      flag: undefined,
      humanHandoff: undefined,
      technicalIssue: undefined,
      page: 1
    });
  };

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<MessageOutlined />}
        title={t('conversationOverview.title')}
        description={t('conversationOverview.description')}
        actions={
          <AutoRefreshToggle
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            isFetching={tenantsFetching || conversationsFetching}
            lastUpdated={lastUpdatedText}
          />
        }
      />

      <ConversationOverviewStats stats={aggregateStats} loading={tenantsLoading} />

      <ConversationOverviewFilters
        values={{
          period: search.period as ConversationOverviewPeriod,
          clientId: search.clientId,
          channel: search.channel,
          flag: search.flag,
          humanHandoff: search.humanHandoff,
          technicalIssue: search.technicalIssue
        }}
        customRange={customRange}
        tenantOptions={tenantOptions}
        hasActiveFilters={hasActiveFilters}
        onChange={updateSearch}
        onClear={clearFilters}
      />

      <ConversationOverviewTable
        conversations={conversationsData?.conversations ?? []}
        clientsById={clientsById}
        loading={conversationsLoading}
        isError={conversationsError}
        page={search.page}
        pageSize={search.pageSize}
        total={conversationsData?.pagination.total ?? 0}
        onPageChange={(nextPage, nextPageSize) =>
          updateSearch({
            page: nextPageSize !== search.pageSize ? 1 : nextPage,
            pageSize: nextPageSize
          })
        }
      />
    </PageTransition>
  );
}
